import {
    useImperativeHandle,
    cloneElement,
    forwardRef,
    useEffect,
    useState,
    useMemo,
    useRef,
    useId
} from "react";
import {
    type AccessibilityActionEvent,
    type LayoutChangeEvent,
    AccessibilityInfo,
    PanResponder,
    Animated,
    Platform,
    View
} from "react-native";
import type ISwipeableRowProps from "./type";
import type {
    SwipeableRowKeyboardEventType,
    SwipeableRowCloseConstantType,
    SwipeableRowRunConstantType,
    SwipeableRowFocusEventType,
    SwipeableRowWebPropsType,
    SwipeableRowLatestType,
    SwipeableRowSide,
    ISwipeableRowRef,
    ISwipeRowAction
} from "./type";
import stylesheet, {
    getSwipeableRowActionWidth,
    getSwipeableRowRadius,
    useStyles
} from "./stylesheet";
import SwipeableRowAction from "./components/action";
import SwipeableRowHover from "./components/hover";
import {
    closeOtherSwipeableRows,
    registerSwipeableRow
} from "./registry";
import {
    SWIPEABLE_ROW_SPRING_VELOCITY_SCALE,
    SWIPEABLE_ROW_REST_DISPLACEMENT,
    getSwipeableRowOrderedActions,
    SWIPEABLE_ROW_COLLAPSE_START,
    getSwipeableRowBoundedOffset,
    getIsSwipeableRowFocusInside,
    SWIPEABLE_ROW_MAX_TRAILING,
    SWIPEABLE_ROW_MAX_LEADING,
    SWIPEABLE_ROW_AXIS_RATIO,
    SWIPEABLE_ROW_REST_SPEED,
    focusSwipeableRowTarget,
    getSwipeableRowTarget,
    SWIPEABLE_ROW_HALF
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast
} from "../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../types";
import {
    useReducedMotion,
    useMotionTiming,
    MOTION_SPRINGS
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    Undo2 as Undo2Icon
} from "lucide-react-native";
import {
    getDirectionSign
} from "../../helpers/direction";
import ActionSheet from "../bottomSheet/components/actionSheet";
import DropdownMenu from "../dropdownMenu";
import ContextMenu from "../contextMenu";
import RowCard from "../rowCard";

const SWIPEABLE_ROW_EMPTY_ACTIONS: Array<ISwipeRowAction> = [];

const SwipeableRowUndoIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <Undo2Icon
    color={customColor}
    size={size}
/>;

const SwipeableRow: RefForwardingComponent<ISwipeableRowRef, ISwipeableRowProps> = ({
    trailingActions: trailingActionsProp = SWIPEABLE_ROW_EMPTY_ACTIONS,
    leadingActions: leadingActionsProp = SWIPEABLE_ROW_EMPTY_ACTIONS,
    isFullSwipeEnabled = false,
    interactionMode = "auto",
    isActionLabelVisible,
    isDisabled = false,
    onFullSwipeReach,
    customLocalize,
    onSwipeClose,
    customTheme,
    description,
    onSwipeOpen,
    children,
    onLayout,
    style,
    title,
    ...props
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const directionSign = getDirectionSign({
        direction
    });

    const pointerType = usePointerType();

    const isReduceMotionEnabled = useReducedMotion();

    const removeTiming = useMotionTiming("listRemove", customTheme);

    const insertTiming = useMotionTiming("listInsert", customTheme);

    const id = useId();

    const isWeb = Platform.OS === "web";

    const isCoarse = pointerType === "coarse";

    const isSwipeEnabled = interactionMode === "swipe" || (interactionMode === "auto" && !isWeb);

    const leadingActions = useMemo(() => leadingActionsProp.slice(0, SWIPEABLE_ROW_MAX_LEADING), [
        leadingActionsProp
    ]);

    const trailingActions = useMemo(() => trailingActionsProp.slice(0, SWIPEABLE_ROW_MAX_TRAILING), [
        trailingActionsProp
    ]);

    const orderedActions = useMemo(() => getSwipeableRowOrderedActions({
        trailingActions,
        leadingActions
    }), [
        trailingActions,
        leadingActions
    ]);

    const hasActions = orderedActions.length > 0 && !isDisabled;

    const actionWidth = getSwipeableRowActionWidth({
        controlHeights,
        spaces
    });

    const leadingWidth = leadingActions.length * actionWidth;

    const trailingWidth = trailingActions.length * actionWidth;

    const rowRadius = getSwipeableRowRadius({
        radiuses,
        borders,
        spaces
    });

    const rowProps = children.props;

    const isRowCardChild = children.type === RowCard;

    const rowTitle = title ?? rowProps.title;

    const rowDescription = description ?? (isRowCardChild ? [
        rowProps.subTitle,
        rowProps.rightTitle
    ].filter((part) => !!part).join(" · ") || undefined : undefined);

    const isShowLabel = isActionLabelVisible ?? !!(description ?? (isRowCardChild ? rowProps.subTitle : undefined));

    const [
        rowWidth,
        setRowWidth
    ] = useState<number>(0);

    const [
        rowHeight,
        setRowHeight
    ] = useState<number>(0);

    const [
        side,
        setSide
    ] = useState<SwipeableRowSide | null>(null);

    const [
        isFull,
        setIsFull
    ] = useState<boolean>(false);

    const [
        isRemoved,
        setIsRemoved
    ] = useState<boolean>(false);

    const [
        isSheetActive,
        setIsSheetActive
    ] = useState<boolean>(false);

    const [
        isPointerInside,
        setIsPointerInside
    ] = useState<boolean>(false);

    const [
        isFocusInside,
        setIsFocusInside
    ] = useState<boolean>(false);

    const [
        isMenuOpen,
        setIsMenuOpen
    ] = useState<boolean>(false);

    const offset = useRef(new Animated.Value(0)).current;

    const collapse = useRef(new Animated.Value(1)).current;

    const offsetRef = useRef<number>(0);

    const startRef = useRef<number>(0);

    const sideRef = useRef<SwipeableRowSide | null>(null);

    const isFullRef = useRef<boolean>(false);

    const settledSideRef = useRef<SwipeableRowSide | null>(null);

    const rowRef = useRef<View>(null);

    const latest = useRef({} as SwipeableRowLatestType);

    const isClosed = side === null;

    const isActionsVisible = isPointerInside || isFocusInside || isMenuOpen;

    const isSwipeActive = isSwipeEnabled && hasActions;

    const isPointerMode = !isSwipeEnabled && hasActions;

    const isContextMenuEnabled = isPointerMode && isWeb;

    const animateTo = ({
        velocity = 0,
        toValue,
        onDone,
        onEnd
    }: {
        onDone?: () => void;
        onEnd?: () => void;
        velocity?: number;
        toValue: number;
    }) => {
        if(isReduceMotionEnabled) {
            offset.setValue(toValue);

            onDone?.();

            onEnd?.();

            return;
        }

        Animated.spring(offset, {
            ...MOTION_SPRINGS.standard,
            restDisplacementThreshold: SWIPEABLE_ROW_REST_DISPLACEMENT,
            velocity: velocity * SWIPEABLE_ROW_SPRING_VELOCITY_SCALE,
            restSpeedThreshold: SWIPEABLE_ROW_REST_SPEED,
            useNativeDriver: false,
            toValue
        }).start(({
            finished
        }) => {
            if(finished) {
                onDone?.();
            }

            onEnd?.();
        });
    };

    const animateCollapse = ({
        toValue,
        onDone
    }: {
        onDone?: () => void;
        toValue: number;
    }) => {
        collapse.stopAnimation();

        const {
            duration,
            easing
        } = toValue === 0 ? removeTiming : insertTiming;

        Animated.timing(collapse, {
            useNativeDriver: false,
            duration,
            toValue,
            easing
        }).start(({
            finished
        }) => {
            if(finished) {
                onDone?.();
            }
        });
    };

    const handleClose = ({
        velocity,
        onEnd
    }: SwipeableRowCloseConstantType = {}) => {
        animateTo({
            onDone: () => {
                if(settledSideRef.current === null) {
                    return;
                }

                settledSideRef.current = null;

                onSwipeClose?.();
            },
            toValue: 0,
            velocity,
            onEnd
        });
    };

    const handleSettle = (nextSide: SwipeableRowSide) => {
        settledSideRef.current = nextSide;

        onSwipeOpen?.(nextSide);

        if(!isWeb) {
            AccessibilityInfo.announceForAccessibility(localize("row-actions-shown"));
        }
    };

    const handleOpen = (nextSide: SwipeableRowSide) => {
        const width = nextSide === "leading" ? leadingWidth : trailingWidth;

        if(!width || isDisabled) {
            return;
        }

        const closing = closeOtherSwipeableRows({
            id
        });

        animateTo({
            onDone: () => closing.then(() => handleSettle(nextSide)),
            toValue: nextSide === "leading" ? width : -width
        });
    };

    const handleRestore = () => {
        animateCollapse({
            onDone: () => setIsRemoved(false),
            toValue: 1
        });

        if(offsetRef.current !== 0) {
            animateTo({
                toValue: 0
            });
        }
    };

    const handleRemove = ({
        isFromFull = false,
        action
    }: SwipeableRowRunConstantType) => {
        const state = {
            isUndone: false
        };

        NCoreUIKitToast.open({
            title: action.undoMessage ?? localize("action-completed"),
            action: {
                icon: SwipeableRowUndoIcon,
                onPress: () => {
                    state.isUndone = true;

                    handleRestore();
                },
                title: localize("undo")
            },
            onClosed: () => {
                if(!state.isUndone) {
                    action.onPress?.();
                }
            },
            isShowAction: true,
            icon: action.icon,
            align: "center",
            type: "neutral",
            side: "bottom"
        });

        const handleCollapse = () => {
            setIsRemoved(true);

            animateCollapse({
                toValue: 0
            });
        };

        if(isFromFull || offsetRef.current === 0) {
            handleCollapse();

            return;
        }

        animateTo({
            onDone: handleCollapse,
            toValue: 0
        });
    };

    const handleRun = ({
        isFromFull = false,
        action
    }: SwipeableRowRunConstantType) => {
        if(action.isUndoable) {
            handleRemove({
                isFromFull,
                action
            });

            return;
        }

        action.onPress?.();

        handleClose();
    };

    const handleRelease = ({
        velocity
    }: {
        velocity: number;
    }) => {
        const target = getSwipeableRowTarget({
            value: offsetRef.current,
            isFullSwipeEnabled,
            trailingWidth,
            leadingWidth,
            velocity,
            rowWidth
        });

        if(target.kind === "close" || !target.side) {
            handleClose({
                velocity
            });

            return;
        }

        const targetSide = target.side;

        if(target.kind === "open") {
            animateTo({
                onDone: () => handleSettle(targetSide),
                toValue: target.offset,
                velocity
            });

            return;
        }

        const action = (targetSide === "leading" ? leadingActions : trailingActions)[0];

        animateTo({
            onDone: () => {
                if(action) {
                    handleRun({
                        isFromFull: true,
                        action
                    });
                }
            },
            toValue: target.offset,
            velocity
        });
    };

    latest.current = {
        hasSwipeActions: leadingWidth + trailingWidth > 0,
        activateDistance: shapeConfigs.dragThreshold,
        isFullSwipeEnabled,
        onFullSwipeReach,
        isSwipeEnabled,
        directionSign,
        handleRelease,
        trailingWidth,
        leadingWidth,
        handleClose,
        isDisabled,
        rowWidth
    };

    useImperativeHandle(ref, () => ({
        close: () => latest.current.handleClose(),
        open: handleOpen
    }));

    useEffect(() => {
        const listenerId = offset.addListener(({
            value
        }) => {
            offsetRef.current = value;

            const nextSide = value === 0 ? null : value > 0 ? "leading" : "trailing";

            if(nextSide !== sideRef.current) {
                sideRef.current = nextSide;

                setSide(nextSide);
            }

            const current = latest.current;

            const nextIsFull = current.isFullSwipeEnabled && current.rowWidth > 0 && Math.abs(value) > current.rowWidth * SWIPEABLE_ROW_HALF;

            if(nextIsFull === isFullRef.current) {
                return;
            }

            isFullRef.current = nextIsFull;

            setIsFull(nextIsFull);

            if(nextIsFull) {
                current.onFullSwipeReach?.();
            }
        });

        const unregister = registerSwipeableRow({
            close: () => new Promise<void>((resolve) => {
                if(offsetRef.current === 0) {
                    resolve();

                    return;
                }

                latest.current.handleClose({
                    onEnd: resolve
                });
            }),
            id
        });

        return () => {
            offset.removeListener(listenerId);

            offset.stopAnimation();

            collapse.stopAnimation();

            unregister();
        };
    }, []);

    const panResponder = useMemo(() => PanResponder.create({
        onMoveShouldSetPanResponder: (_, gesture) => {
            const current = latest.current;

            if(!current.isSwipeEnabled || current.isDisabled || !current.hasSwipeActions) {
                return false;
            }

            return Math.abs(gesture.dx) > current.activateDistance && Math.abs(gesture.dx) > Math.abs(gesture.dy) * SWIPEABLE_ROW_AXIS_RATIO;
        },
        onPanResponderMove: (_, gesture) => {
            const current = latest.current;

            offset.setValue(getSwipeableRowBoundedOffset({
                value: startRef.current + gesture.dx * current.directionSign,
                isFullSwipeEnabled: current.isFullSwipeEnabled,
                trailingWidth: current.trailingWidth,
                leadingWidth: current.leadingWidth,
                rowWidth: current.rowWidth
            }));
        },
        onPanResponderRelease: (_, gesture) => {
            latest.current.handleRelease({
                velocity: gesture.vx * latest.current.directionSign
            });
        },
        onPanResponderTerminationRequest: () => false,
        onPanResponderGrant: () => {
            offset.stopAnimation();

            startRef.current = offsetRef.current;

            closeOtherSwipeableRows({
                id
            });
        },
        onPanResponderTerminate: () => {
            latest.current.handleRelease({
                velocity: 0
            });
        }
    }), []);

    const translateX = useMemo(() => offset.interpolate({
        outputRange: [
            -directionSign,
            directionSign
        ],
        inputRange: [
            -1,
            1
        ]
    }), [
        directionSign
    ]);

    const leadingAnimatedWidth = useMemo(() => offset.interpolate({
        extrapolateLeft: "clamp",
        outputRange: [
            0,
            1
        ],
        inputRange: [
            0,
            1
        ]
    }), []);

    const trailingAnimatedWidth = useMemo(() => offset.interpolate({
        extrapolateRight: "clamp",
        outputRange: [
            1,
            0
        ],
        inputRange: [
            -1,
            0
        ]
    }), []);

    const collapseHeight = useMemo(() => collapse.interpolate({
        inputRange: [
            0,
            SWIPEABLE_ROW_COLLAPSE_START,
            1
        ],
        outputRange: [
            0,
            rowHeight,
            rowHeight
        ]
    }), [
        rowHeight
    ]);

    const {
        actionsLayer: actionsLayerDynamicStyle,
        container: containerDynamicStyle,
        root: rootDynamicStyle
    } = useStyles({
        isReserveEndEnabled: isPointerMode && isCoarse,
        isSwipeEnabled: isSwipeActive,
        borderRadius: rowRadius,
        controlHeights,
        shapeConfigs,
        isRemoved,
        isClosed,
        colors,
        spaces
    });

    const handleLayout = (event: LayoutChangeEvent) => {
        setRowWidth(event.nativeEvent.layout.width);

        if(!isRemoved) {
            setRowHeight(event.nativeEvent.layout.height);
        }

        onLayout?.(event);
    };

    const handleRowPress = () => {
        if(!isClosed) {
            handleClose();

            return;
        }

        rowProps.onPress?.();
    };

    const handleRowLongPress = () => {
        if(!isClosed) {
            handleClose();

            return;
        }

        setIsSheetActive(true);
    };

    const handleAccessibilityAction = (event: AccessibilityActionEvent) => {
        const action = orderedActions.find((item) => item.key === event.nativeEvent.actionName);

        if(action) {
            handleRun({
                action
            });

            return;
        }

        rowProps.onAccessibilityAction?.(event);
    };

    const handleActionPress = (action: ISwipeRowAction) => {
        handleRun({
            action
        });
    };

    const onKeyDown = (event: SwipeableRowKeyboardEventType) => {
        if(event.key !== "Escape" || isClosed) {
            return;
        }

        handleClose();

        focusSwipeableRowTarget({
            node: rowRef.current
        });
    };

    const onFocus = () => {
        setIsFocusInside(true);
    };

    const onBlur = (event: SwipeableRowFocusEventType) => {
        if(getIsSwipeableRowFocusInside({
            relatedTarget: event.relatedTarget,
            currentTarget: event.currentTarget
        })) {
            return;
        }

        setIsFocusInside(false);
    };

    const webProps: SwipeableRowWebPropsType = isWeb ? {
        onKeyDown,
        onFocus,
        onBlur
    } : {};

    const menuItems = orderedActions.flatMap((action, index) => {
        const items = [
            <DropdownMenu.Item
                type={action.type === "danger" ? "danger" : "neutral"}
                onPress={() => handleRun({
                    action
                })}
                shortcut={action.shortcut}
                customTheme={customTheme}
                title={action.title}
                icon={action.icon}
                key={action.key}
            />
        ];

        if(action.type === "danger" && index > 0 && orderedActions[index - 1]?.type !== "danger") {
            items.unshift(<DropdownMenu.Separator
                key={`${action.key}-separator`}
            />);
        }

        return items;
    });

    const sheetItems = orderedActions.map((action) => ({
        type: action.type === "danger" ? "danger" as const : "neutral" as const,
        onPress: () => handleRun({
            action
        }),
        label: action.title,
        icon: action.icon,
        key: action.key
    }));

    const renderAction = (action: ISwipeRowAction, index: number, actionSide: SwipeableRowSide) => {
        return <SwipeableRowAction
            isFull={isFull && side === actionSide}
            onPress={handleActionPress}
            isShowLabel={isShowLabel}
            customTheme={customTheme}
            isFocusable={!isClosed}
            isFirst={index === 0}
            width={actionWidth}
            side={actionSide}
            key={action.key}
            action={action}
        />;
    };

    const renderRow = () => {
        const isRowInteractive = !!rowProps.onPress || !isClosed;

        return cloneElement(children, {
            onAccessibilityAction: isSwipeActive && !isWeb ? handleAccessibilityAction : rowProps.onAccessibilityAction,
            onPress: isRowInteractive && isSwipeActive ? handleRowPress : rowProps.onPress,
            delayLongPress: isSwipeActive ? durations.longPress : rowProps.delayLongPress,
            ...isRowCardChild ? {
                rightContainerStyle: isPointerMode && !isCoarse && isActionsVisible ? [
                    rowProps.rightContainerStyle,
                    stylesheet.metaHidden
                ] : rowProps.rightContainerStyle
            } : {},
            onLongPress: isSwipeActive ? handleRowLongPress : rowProps.onLongPress,
            borderRadius: isClosed || !isSwipeActive ? rowRadius : 0,
            accessibilityActions: isSwipeActive && !isWeb ? [
                ...rowProps.accessibilityActions ?? [],
                ...orderedActions.map((action) => ({
                    label: action.title,
                    name: action.key
                }))
            ] : rowProps.accessibilityActions
        });
    };

    const renderContent = () => {
        if(!isSwipeActive) {
            return renderRow();
        }

        return <Animated.View
            style={{
                transform: [
                    {
                        translateX
                    }
                ]
            }}
            {...panResponder.panHandlers}
            ref={rowRef}
        >
            {renderRow()}
        </Animated.View>;
    };

    const renderLayer = () => {
        if(!isSwipeActive) {
            return null;
        }

        return <View
            style={[
                stylesheet.layer,
                actionsLayerDynamicStyle
            ]}
            aria-hidden={isClosed}
        >
            <Animated.View
                style={[
                    stylesheet.side,
                    {
                        width: leadingAnimatedWidth
                    }
                ]}
            >
                {leadingActions.map((action, index) => renderAction(action, index, "leading"))}
            </Animated.View>
            <Animated.View
                style={[
                    stylesheet.side,
                    stylesheet.trailingSide,
                    {
                        width: trailingAnimatedWidth
                    }
                ]}
            >
                {trailingActions.map((action, index) => renderAction(action, index, "trailing"))}
            </Animated.View>
        </View>;
    };

    const renderHover = () => {
        if(!isPointerMode) {
            return null;
        }

        return <SwipeableRowHover
            isVisible={isActionsVisible || isCoarse}
            onActionPress={handleActionPress}
            onMenuOpenChange={setIsMenuOpen}
            customLocalize={customLocalize}
            isHovered={isPointerInside}
            customTheme={customTheme}
            borderRadius={rowRadius}
            actions={orderedActions}
            menuItems={menuItems}
            isCoarse={isCoarse}
            title={rowTitle}
        />;
    };

    const renderSheet = () => {
        if(!isSwipeActive) {
            return null;
        }

        return <ActionSheet
            onClosed={() => setIsSheetActive(false)}
            customLocalize={customLocalize}
            description={rowDescription}
            customTheme={customTheme}
            isActive={isSheetActive}
            items={sheetItems}
            title={rowTitle}
        />;
    };

    const renderBody = () => {
        const content = <View
            {...props}
            {...webProps}
            onPointerLeave={isPointerMode ? () => setIsPointerInside(false) : undefined}
            onPointerEnter={isPointerMode ? () => setIsPointerInside(true) : undefined}
            onLayout={handleLayout}
            style={[
                style,
                stylesheet.root,
                rootDynamicStyle
            ]}
        >
            {renderLayer()}
            {renderContent()}
            {renderHover()}
            {renderSheet()}
        </View>;

        if(!isContextMenuEnabled) {
            return content;
        }

        return <ContextMenu
            customLocalize={customLocalize}
            customTheme={customTheme}
            title={rowTitle}
        >
            <ContextMenu.Trigger>
                {content}
            </ContextMenu.Trigger>
            <ContextMenu.Content>
                {menuItems}
            </ContextMenu.Content>
        </ContextMenu>;
    };

    return <Animated.View
        style={isRemoved ? [
            stylesheet.collapse,
            containerDynamicStyle,
            {
                height: collapseHeight,
                opacity: collapse
            }
        ] : containerDynamicStyle}
        aria-hidden={isRemoved}
    >
        {renderBody()}
    </Animated.View>;
};
export default forwardRef(SwipeableRow);
